import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import { invalidateBookingScopedQueries } from 'app/data/mutations/invalidate-booking-scoped-queries';
import { authTokenStore, MAX_AUTH_RETRIES } from 'app/shared/auth';
import { useApi } from 'app/shared/contexts/api';
import { useUser } from 'app/shared/contexts/user';
import { isAuthError } from 'app/shared/utils/is-auth-error';

/*
 * search-service pushes a Server-Sent-Events frame per activity change for the current user, so this
 * miniapp learns about a booking that was cancelled or checked in SOMEWHERE ELSE — the activity centre,
 * the resource-booking receipt, the mobile app — without the user refreshing.
 *
 * A frame carries the raw domain event, NOT the shape these pages render, so it is treated purely as a
 * SIGNAL TO RE-READ: it triggers exactly the invalidation every booking mutation already performs
 * (`invalidateBookingScopedQueries`), rather than patching a booking in from the frame body. One refresh
 * path, so a pushed change and a locally-initiated one can never diverge.
 *
 * Invalidation is the right tool HERE, where the sibling activity-centre feed needed a bespoke silent
 * patch instead: every query this touches is a plain `useQuery`, so an invalidation is a background
 * refetch that leaves `data` and `isLoading` exactly as they are — nothing in this app renders off
 * `isFetching`, so no spinner appears and no list moves. The feed's problem was specific to a
 * cursor-paginated infinite query, whose refetch re-runs the whole page chain and re-slices the windows.
 */
/*
 * `includeAllEventStatuses` opts this stream in to event frames of EVERY status.
 *
 * It defaults to false server-side because the SSE endpoints are shared with users-admin, which cannot
 * yet label waitlisted or cancelled registrations. Without opting in, the most important frame for this
 * app would be the one it never receives: cancelling a registration elsewhere would push nothing, and
 * the CTA would keep offering to cancel a booking that is already gone.
 */
const activityStreamUrl = (searchUrl: string, userUuid: string): string =>
  `${searchUrl}/search/v1/activity-events/events/${userUuid}?includeAllEventStatuses=true`;

/*
 * The gateway fronts search-service in every deployed environment, so the app's own api origin is the
 * right host. Locally the miniapp talks to port 3000 while search-service listens on 3035, and there is
 * no gateway in between — hence the rewrite, which is a no-op anywhere else.
 */
export const resolveSearchUrl = (apiUrl: string): string => apiUrl.replace('localhost:3000', 'localhost:3035');

// The stream interleaves `event: heartbeat` frames (empty data) every 25s so the load balancer doesn't
// drop the idle connection. They carry no change and must not trigger a refresh.
const HEARTBEAT_EVENT = 'heartbeat';

// Coalesce a burst of frames (a multi-session booking lands as several) into a single refresh.
const REFRESH_DEBOUNCE_MS = 400;

// Reconnect backoff: a dropped stream retries, growing to a cap so a persistent outage doesn't hammer.
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

/**
 * Half the delay fixed, half random.
 *
 * A search-service deploy drops every client's stream at the same instant, so an exact backoff has them
 * all return together — repeatedly, and hardest at the moment the new pods are coldest. The random half
 * spreads that herd out; the fixed half keeps the growth meaningful.
 */
const withJitter = (delay: number): number => delay / 2 + Math.random() * (delay / 2);

/**
 * How long silence is allowed before the connection is presumed dead.
 *
 * A dropped TCP connection does not always arrive as an error — a sleeping laptop, a network change, a
 * load balancer reaping an idle socket all leave a read that simply never resolves. The hook would sit
 * on it forever, connected in name only: no frames, no error, no reconnect. That matters more in a
 * webview than in a tab, because a miniapp is routinely suspended and resumed by its host.
 *
 * The server heartbeats every 25s (`HEARTBEAT_INTERVAL_MS` in search-service), so this is a multiple of
 * that, not an absolute duration. THREE missed beats plus slack, deliberately, because the cost of the
 * two mistakes is wildly asymmetric:
 *
 * - Too slow, and a genuinely dead pipe is noticed a few seconds later than it could have been.
 * - Too fast, and a HEALTHY connection is killed. That one compounds: `reconnectAttempt` only resets
 *   when bytes arrive, and the watchdog fires precisely when they have not, so each spurious kill
 *   reconnects on a longer backoff than the last — 1s, 2s, 4s, out to the 30s cap. Every change that
 *   lands inside those widening gaps is simply never seen, which reads to a user as the stream working
 *   intermittently, or working for some booking types and not others.
 *
 * A single delayed beat — a GC pause, a throttled timer in a backgrounded webview, a slow pod — must
 * therefore never be enough to trip this.
 */
export const STREAM_IDLE_TIMEOUT_MS = 90_000;

/*
 * SSE permits CRLF, CR or LF as a line ending — independently — so a blank-line frame boundary can be
 * any two consecutive endings, INCLUDING mixed pairs like `\n\r\n`. Matching only `\n\n` leaves a CRLF
 * frame sitting in the buffer forever: the refresh never fires and the app silently stops updating,
 * with a healthy-looking connection still open.
 *
 * Every ending is normalized to `\n` as bytes arrive, so a boundary is always `\n\n`, rather than
 * enumerating the pairs in a separator regex — a `(?:\r\n|\r|\n){2}` alternation backtracks and splits
 * a lone CRLF, and spelling the pairs out still mis-slices a boundary whose first half is `\r`.
 *
 * A lone trailing `\r` is carried into the next read rather than normalized now: it may be the front
 * half of a CRLF split across two network chunks, and collapsing it early would fake a boundary against
 * the next chunk's `\n`.
 */
const FRAME_LINE = /\r\n|\r|\n/;
const CARRIAGE_RETURN = '\r';
const normalizeLineEndings = (text: string): string => text.replace(/\r\n|\r/g, '\n');

/**
 * Make an await that has no cancellation of its own end when `signal` aborts.
 *
 * `fetch` takes the signal and so honours the idle watchdog for free. The token read does not.
 * `getAuthHeaders()` resolves through `authTokenStore.getToken()`, which deliberately waits for an
 * in-flight refresh — and since SMB-1565 also STARTS one when the held token is near expiry — and a
 * refresh is an SDK `refreshToken` round-trip to the native host over postMessage. Aborting the
 * controller while that is the pending await has no listener to reject: the attempt sits inside the
 * `try` forever, the `catch` never runs, no reconnect is ever scheduled, `finally` never clears the
 * timer, and the stream is gone for the rest of the session behind a watchdog that already fired.
 *
 * A host that never answers that message is a routine webview failure, not an exotic one — the host
 * suspends and resumes these views at will — so this is the client most exposed to it.
 *
 * Racing the signal closes that. It does NOT cancel the underlying round-trip, which there is no API
 * to cancel; and because the store shares one in-flight refresh between callers, a genuinely wedged
 * host hands the same pending promise to the next attempt, which will abort again on the next
 * watchdog. That is the intended outcome rather than a shortcoming: the retry loop keeps running at
 * the idle interval and recovers the moment that promise settles, instead of the connection being
 * lost for good the first time a refresh hangs.
 */
const abortable = async <T>(promise: Promise<T>, signal: AbortSignal): Promise<T> => {
  if (signal.aborted) throw new Error('activity stream aborted before the auth token was read');

  let onAbort: (() => void) | undefined;

  // NB: no blank line before this `return` despite the house rule in `.cursor/rules/generic.mdc` —
  // it is the first statement in the block, where eslint's `padded-blocks` forbids one. Lint wins.
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_resolve, reject) => {
        onAbort = () => reject(new Error('activity stream aborted while reading the auth token'));
        signal.addEventListener('abort', onAbort, { once: true });
      }),
    ]);
  } finally {
    if (onAbort) signal.removeEventListener('abort', onAbort);
  }
};

const isHeartbeatFrame = (frame: string): boolean =>
  frame
    .split(FRAME_LINE)
    .some((line) => line.startsWith('event:') && line.slice('event:'.length).trim() === HEARTBEAT_EVENT);

/**
 * Hold one SSE connection to search-service for the current user and refresh the booking-scoped queries
 * whenever a real change lands, so a cancel or check-in performed on another surface is reflected here
 * without a reload.
 *
 * Fetch-based rather than `EventSource` because the read needs the app's `Authorization` header, which
 * `EventSource` cannot set; the gateway accepts the same headers every other call already uses. Mount
 * once, app-wide — it has no return value, driving the shared query cache through invalidation.
 */
export const useBookingActivityStream = (): void => {
  const { user } = useUser();
  const { apiUrl, getAuthHeaders } = useApi();
  const queryClient = useQueryClient();

  const userUuid = user?.uuid;
  const searchUrl = apiUrl ? resolveSearchUrl(apiUrl) : '';

  const refresh = useCallback(() => invalidateBookingScopedQueries(queryClient), [queryClient]);

  /*
   * Latest values held in refs so the connection effect depends only on identity (user/url), not on the
   * fresh function references React hands us each render — otherwise it would tear down and reconnect on
   * every parent re-render. Reading the auth getter through a ref also means a reconnect after a token
   * refresh (see `useAuthTokenSync`) picks up the new value.
   *
   * Filled in an EFFECT rather than during render. A ref written mid-render is not undone when React
   * discards that render — and it discards them routinely under StrictMode and concurrent rendering — so
   * the connection could go on to reconnect using a value from a render that never committed. Writing
   * after commit means the ref only ever holds something the tree actually adopted.
   *
   * Declared above the connection effect on purpose: effects run in declaration order, so both refs hold
   * the committed value before the first connection opens.
   */
  const refreshRef = useRef(refresh);
  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  const getAuthHeadersRef = useRef(getAuthHeaders);
  useEffect(() => {
    getAuthHeadersRef.current = getAuthHeaders;
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!userUuid || !searchUrl) return undefined;

    /*
     * Torn-down state and a handle on the connection currently in flight, rather than one shared
     * AbortController. The idle watchdog has to abandon a SINGLE dead connection without disarming
     * teardown — and a per-attempt controller chained to an outer one by event listener is more
     * lifecycle than this needs.
     */
    let isTornDown = false;
    let currentAttempt: AbortController | undefined;
    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let reconnectAttempt = 0;
    /*
     * Whether any attempt has already failed, which is the whole question the catch-up asks: a
     * connection that replaces a previous one may have missed changes while it was down.
     *
     * Tracked separately from `reconnectAttempt` even though that counter would usually agree,
     * because it is bookkeeping for a DIFFERENT question — how long to wait — and the auth recovery
     * below deliberately rewinds it. The two only stayed consistent by virtue of the unconditional
     * increment sitting below that rewind, which is an invariant nothing states and one edit could
     * quietly break, costing a silent catch-up and a stale booking with no test to notice.
     */
    let hasAttemptFailed = false;
    /*
     * Auth recoveries spent since this connection last worked. Bounded by the same `MAX_AUTH_RETRIES`
     * the shared query policy uses, so a host that cannot mint a token backs off like any other
     * outage instead of spinning — and reset on bytes, so a session outliving several token
     * lifetimes recovers from each expiry rather than only the first.
     */
    let authRefreshesUsed = 0;

    const scheduleRefresh = (): void => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        refreshRef.current();
      }, REFRESH_DEBOUNCE_MS);
    };

    const readStream = async (body: ReadableStream<Uint8Array>, onBytes: () => void): Promise<void> => {
      const reader = body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      // A lone `\r` at the end of a chunk may be the front half of a CRLF still in flight — hold it.
      let heldCarriageReturn = false;
      let streamDone = false;
      while (!streamDone) {
        // Sequential by nature — each SSE frame is read off the socket in order.
        // eslint-disable-next-line no-await-in-loop
        const { value, done } = await reader.read();
        streamDone = done;
        if (value) {
          // ANY bytes prove the pipe is alive, heartbeats included — that is what they are for.
          onBytes();
          const decoded: string = (heldCarriageReturn ? CARRIAGE_RETURN : '') + decoder.decode(value, { stream: true });
          heldCarriageReturn = decoded.endsWith(CARRIAGE_RETURN);
          buffer += normalizeLineEndings(heldCarriageReturn ? decoded.slice(0, -1) : decoded);
          let boundary = buffer.indexOf('\n\n');
          while (boundary !== -1) {
            const frame = buffer.slice(0, boundary);
            if (frame && !isHeartbeatFrame(frame)) scheduleRefresh();
            buffer = buffer.slice(boundary + 2);
            boundary = buffer.indexOf('\n\n');
          }
        }
      }
    };

    const connect = async (): Promise<void> => {
      // One controller per attempt, so the watchdog can drop this connection and no other.
      const attempt = new AbortController();
      currentAttempt = attempt;

      let idleTimer: ReturnType<typeof setTimeout> | undefined;
      const armIdleWatchdog = (): void => {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(() => attempt.abort(), STREAM_IDLE_TIMEOUT_MS);
      };

      /*
       * Armed BEFORE the awaits below, not after the handshake returns. Neither `getAuthHeaders()` nor
       * `fetch` has a timeout of its own, so a token read or a connect that never settles — a laptop
       * that slept mid-handshake, a network change — would otherwise hang here with no watchdog running
       * at all: no frames, no error, and nothing to notice it.
       *
       * Arming it is only half the job: aborting the controller ends the `fetch`, which holds the
       * signal, but it cannot end the token read, which does not — hence `abortable`.
       */
      armIdleWatchdog();

      try {
        const headers = await abortable(getAuthHeadersRef.current(), attempt.signal);
        const response = await fetch(activityStreamUrl(searchUrl, userUuid), {
          method: 'GET',
          headers: { ...headers, Accept: 'text/event-stream' },
          signal: attempt.signal,
        });
        if (!response.ok || !response.body) {
          // `statusCode` is what `isAuthError` reads. Raw `fetch` doesn't produce an `ApiError` for
          // free, so it is attached by hand — the same thing the QR query does for the same reason.
          throw Object.assign(new Error(`activity stream responded ${response.status}`), {
            statusCode: response.status,
          });
        }

        // A successful RECONNECT (not the first connect) may have missed changes while it was down —
        // catch up once. The first connect is skipped: the queries' own initial fetch already holds
        // fresh data, and re-invalidating it would double every cold start.
        if (hasAttemptFailed) refreshRef.current();
        armIdleWatchdog();

        await readStream(response.body, () => {
          /*
           * The backoff resets HERE, on bytes actually arriving — not on the handshake returning.
           *
           * A gateway that accepts the request and then closes it (istio or a load balancer that has
           * not been told about `text/event-stream` does exactly this) answers 200 every time.
           * Resetting on that produces a connection whose backoff never grows: a reconnect every
           * second, and with it a full booking refetch every second, from every open client — a load
           * spike aimed at the service that was already unwell.
           */
          reconnectAttempt = 0;
          authRefreshesUsed = 0;
          armIdleWatchdog();
        });
        // A closed stream is not the steady state — fall through to reconnect below.
        throw new Error('activity stream closed');
      } catch (error) {
        if (isTornDown) return;
        // Whatever the reason, this connection did not survive — the next one is a replacement and
        // owes the caller a catch-up. Set before any of the recovery bookkeeping below touches it.
        hasAttemptFailed = true;

        /*
         * A 401 is the one failure waiting longer cannot fix, and this stream is the surface most
         * likely to meet one: it is the only request here that stays open across a token's whole
         * lifetime, while every other call in the app is made in response to something the user just
         * did. Nothing refreshes the token on a schedule — `useAuthTokenSync` only registers the
         * refresher, and `retryOnAuthError` fires it when a QUERY sees a 401 — so a pane left open
         * past expiry would otherwise reconnect on the same dead token until the user happened to
         * touch something, widening the backoff to the 30s cap on the way. That is precisely the
         * passive case the stream exists to serve.
         *
         * So kick the same refresh the shared retry policy kicks, and come back on the base delay
         * rather than a widening one. `getAuthHeaders()` awaits the in-flight refresh, so the next
         * attempt carries the new bearer without this having to sequence anything.
         */
        if (isAuthError(error) && authRefreshesUsed < MAX_AUTH_RETRIES) {
          authRefreshesUsed += 1;
          // Fire-and-forget, like `retryOnAuthError`: the store dedupes concurrent refreshes and
          // never rejects, handing a persistent failure to its own `onRefreshFailed` (an SDK logout).
          authTokenStore.refresh();
          reconnectAttempt = 0;
        }

        const delay = withJitter(Math.min(RECONNECT_BASE_MS * 2 ** reconnectAttempt, RECONNECT_MAX_MS));
        reconnectAttempt += 1;
        reconnectTimer = setTimeout(() => {
          connect().catch(() => undefined);
        }, delay);
      } finally {
        if (idleTimer) clearTimeout(idleTimer);
      }
    };

    connect().catch(() => undefined);

    return () => {
      isTornDown = true;
      currentAttempt?.abort();
      if (debounceTimer) clearTimeout(debounceTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [userUuid, searchUrl]);
};
